<template>
  <div class="sms-query-page">
    <section class="sms-query-hero">
      <p class="eyebrow">AUTHORIZED SMS LOOKUP</p>
      <h2>Find recent SMS records</h2>
      <p>Enter a phone number to view its three most recent SMS records.</p>

      <el-input
        v-model.trim="phone"
        class="sms-query-input"
        clearable
        prefix-icon="el-icon-search"
        placeholder="Phone number, e.g. +234 801 234 5678"
        autocomplete="off"
        inputmode="tel"
        @input="scheduleSearch"
        @clear="clearResults"
      />
      <small class="sms-query-hint">International, country-code, leading-zero and local numbers without the leading zero are supported.</small>
    </section>

    <section v-if="loading || searched" class="sms-query-results" aria-live="polite">
      <div v-if="loading" class="sms-query-state">
        <i class="el-icon-loading"></i>
        <span>Searching SMS records…</span>
      </div>

      <div v-else-if="errorMessage" class="sms-query-state sms-query-error">
        <i class="el-icon-warning-outline"></i>
        <span>{{ errorMessage }}</span>
      </div>

      <div v-else-if="items.length === 0" class="sms-query-state">
        <i class="el-icon-document"></i>
        <span>No SMS records found for this phone number.</span>
      </div>

      <template v-else>
        <div class="sms-query-results-head">
          <div>
            <p class="eyebrow">LATEST RESULTS</p>
            <h3>{{ items.length }} SMS record{{ items.length === 1 ? '' : 's' }}</h3>
          </div>
          <span>Newest first</span>
        </div>

        <article v-for="item in items" :key="item.messageId" class="sms-record-card">
          <div class="sms-record-topline">
            <time>{{ formatDate(item.createdAt) }}</time>
            <span class="sms-status" :class="statusTone(item)">{{ statusLabel(item) }}</span>
          </div>
          <div class="sms-record-content-row">
            <p class="sms-record-content">{{ item.renderedContent || 'Message content is unavailable for this historical record.' }}</p>
            <el-button
              class="sms-copy-button"
              type="text"
              icon="el-icon-document-copy"
              :disabled="!item.renderedContent"
              @click="copyContent(item.renderedContent)"
            >Copy</el-button>
          </div>
          <dl class="sms-record-meta">
            <div><dt>Phone</dt><dd>{{ item.receiverPhone || item.receiverMasked }}</dd></div>
            <div><dt>Channel</dt><dd>{{ item.smsChannel || '—' }}</dd></div>
            <div><dt>Sender ID</dt><dd>{{ item.senderId || '—' }}</dd></div>
            <div><dt>Source</dt><dd>{{ item.sourceSystem || '—' }}</dd></div>
          </dl>
        </article>
      </template>
    </section>
  </div>
</template>

<script>
import { getLatestSmsByPhone } from '../api/admin'

export default {
  name: 'SmsRecordQueryView',
  data () {
    return {
      phone: '',
      items: [],
      loading: false,
      searched: false,
      errorMessage: '',
      searchTimer: null,
      requestSequence: 0
    }
  },
  beforeDestroy () {
    window.clearTimeout(this.searchTimer)
  },
  methods: {
    scheduleSearch () {
      window.clearTimeout(this.searchTimer)
      const sequence = ++this.requestSequence
      this.errorMessage = ''
      const digits = this.phone.replace(/\D/g, '')
      if (digits.length < 7) {
        this.items = []
        this.searched = false
        this.loading = false
        return
      }
      this.searchTimer = window.setTimeout(() => this.search(sequence), 350)
    },
    async search (sequence) {
      this.loading = true
      this.searched = true
      this.errorMessage = ''
      try {
        const result = await getLatestSmsByPhone(this.phone)
        if (sequence === this.requestSequence) this.items = result
      } catch (error) {
        if (sequence !== this.requestSequence) return
        this.items = []
        this.errorMessage = (error.response && error.response.data && error.response.data.message) ||
          'Unable to search SMS records. Please try again.'
      } finally {
        if (sequence === this.requestSequence) this.loading = false
      }
    },
    clearResults () {
      window.clearTimeout(this.searchTimer)
      this.requestSequence += 1
      this.items = []
      this.loading = false
      this.searched = false
      this.errorMessage = ''
    },
    statusLabel (item) {
      const status = (item.finalStatus || item.status || '').toLowerCase()
      const labels = {
        success: 'Delivered', final_success: 'Delivered', delivered: 'Delivered', delivrd: 'Delivered',
        failed: 'Failed', final_failed: 'Failed', rejected: 'Failed',
        sending: 'Sending', accepted: 'Accepted', pending: 'Pending', cancelled: 'Cancelled'
      }
      return labels[status] || status.replace(/_/g, ' ').replace(/^./, value => value.toUpperCase()) || 'Unknown'
    },
    statusTone (item) {
      const label = this.statusLabel(item)
      if (label === 'Delivered') return 'is-success'
      if (label === 'Failed' || label === 'Cancelled') return 'is-danger'
      return 'is-pending'
    },
    async copyContent (content) {
      if (!content) return
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(content)
        } else {
          this.copyWithFallback(content)
        }
        this.$message.success('Message content copied.')
      } catch (error) {
        this.$message.error('Unable to copy the message content.')
      }
    },
    copyWithFallback (content) {
      const input = document.createElement('textarea')
      input.value = content
      input.setAttribute('readonly', '')
      input.style.position = 'fixed'
      input.style.opacity = '0'
      document.body.appendChild(input)
      let copied = false
      try {
        input.select()
        copied = document.execCommand('copy')
      } finally {
        document.body.removeChild(input)
      }
      if (!copied) throw new Error('Copy command was rejected')
    },
    formatDate (value) {
      if (!value) return 'Time unavailable'
      const parsed = new Date(value)
      return Number.isNaN(parsed.getTime()) ? value.replace('T', ' ') : parsed.toLocaleString('en-GB')
    }
  }
}
</script>
